import { Fragment } from 'react';
import { Grid } from '@mui/material';
import { Icon, Row } from 'vetrf-ui';
import { EntryType } from 'components/registry/types/api';
import { CatalogLink } from 'modules/catalogs/components/CatalogLink';
import { CerberusIntegrationStatus } from 'components/CerberusIntegrationStatus';
import { CerberusIntegrationError } from 'components/CerberusIntegrationError';
import { ErulRegistrationCodes } from 'api/erul/types';
import { getMessageForElkEpgu } from 'api/elkEpgu/utils';
import { ProducerType } from '../../../../types/types';
import { InformationTable } from './types';
import { ProducerLocationsLabel } from './ProducerLocationsLabel';

export const mainBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => (
      <>
        <p>
          <CatalogLink
            href={`/#/catalogs/business-entities/${item.recipientId}`}
            target="_blank"
            rel="noreferrer"
          >
            {item.recipientName}
          </CatalogLink>
        </p>
        {item.inn && (
          <p>
            <b>ИНН:</b> {item.inn}
          </p>
        )}
        {item.ogrn && (
          <p>
            <b>ОГРН:</b> {item.ogrn}
          </p>
        )}
        <CerberusIntegrationError showMessage={item.recipientIncorrectCerberusLink} />
        <CerberusIntegrationStatus isNotLinkedWithCerberus={item.recipientNotLinkedWithCerberus} />
      </>
    ),
    header: {
      key: 'recipientName',
      title: 'Наименование ХС',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.registryProductName,
    header: {
      key: 'registryProductName',
      title: 'Название ЛС',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.registryProductInterName,
    header: {
      key: 'registryProductInterName',
      title: 'Международное непатентованное наименование',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.regNumber,
    header: {
      key: 'regNumber',
      title: 'Номер регистрационного удостоверения',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.activeSubstanceName,
    header: {
      key: 'activeSubstanceName',
      title: 'Действующее вещество',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.releaseForm,
    header: {
      key: 'releaseForm',
      title: 'Лекарственная форма',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.releaseVolume,
    header: {
      key: 'releaseVolume',
      title: 'Объем, введенный в оборот',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.measuringUnit,
    header: {
      key: 'measuringUnit',
      title: 'Единицы измерения объема',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.packType,
    header: {
      key: 'packType',
      title: 'Фасовка/упаковка',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.packCount,
    header: {
      key: 'packCount',
      title: 'Количество единиц упаковок',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.packDoses,
    header: {
      key: 'packDoses',
      title: 'Количество доз в упаковке',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.dosage,
    header: {
      key: 'dosage',
      title: 'Дозировка',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.packSerialNumber,
    header: {
      key: 'packSerialNumber',
      title: 'Номер серии/партии',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      item.producerList?.map((producer: ProducerType) => (
        <Fragment key={producer.id}>
          <ProducerLocationsLabel producer={producer} />
          <br />
        </Fragment>
      )),
    header: {
      key: 'producerList',
      title: 'Производственная площадка производителя',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.authorizedPerson,
    header: {
      key: 'authorizedPerson',
      title: 'Сведения об уполномоченном лице',
    },
    mode: [EntryType.requests, EntryType.registry],
    isRegistred: true,
  },
  {
    dataFormatter: (item) => (
      <Grid container spacing="4">
        {item.qualityDocumentInformation?.qualityDocNumber && (
          <Grid item>
            <p>{`Номер: ${item.qualityDocumentInformation?.qualityDocNumber}`}</p>
          </Grid>
        )}
        {item.qualityDocumentInformation?.qualityDocDate && (
          <Grid item marginLeft={item.qualityDocumentInformation?.qualityDocNumber ? 2 : 0}>
            <p>{`Дата: ${item.qualityDocumentInformation?.qualityDocDate}`}</p>
          </Grid>
        )}
        {item.qualityDocumentInformation?.qualityDocFile?.id && (
          <Grid item marginLeft={2}>
            <p>
              <span>Файл: </span>
              <a href={`/files/${item.qualityDocumentInformation?.qualityDocFile?.id}`}>
                <Icon icon="download" />
                <span>{item.qualityDocumentInformation?.qualityDocFile?.name}</span>
              </a>
            </p>
          </Grid>
        )}
      </Grid>
    ),
    header: {
      key: 'qualityDocumentInformation',
      title: 'Документ производителя ЛС, подтверждающий соответствие качества',
    },
    mode: [EntryType.requests, EntryType.registry],
    isRegistred: true,
  },
  {
    dataFormatter: (item) => (
      <Grid container spacing="4">
        {item.confirmDocumentInformation?.confirmDocNumber && (
          <Grid item>
            <p>{`Номер решения: ${item.confirmDocumentInformation?.confirmDocNumber}`}</p>
          </Grid>
        )}
        {item.confirmDocumentInformation?.confirmDocDate && (
          <Grid item marginLeft={item.confirmDocumentInformation?.confirmDocNumber ? 2 : 0}>
            <p>{`Дата: ${item.confirmDocumentInformation?.confirmDocDate}`}</p>
          </Grid>
        )}
        {item.confirmDocumentInformation?.confirmDocFile?.id && (
          <Grid item marginLeft={2}>
            <p>
              <span>Файл: </span>
              <a href={`/files/${item.confirmDocumentInformation?.confirmDocFile?.id}`}>
                <Icon icon="download" />
                <span>{item.confirmDocumentInformation?.confirmDocFile?.name}</span>
              </a>
            </p>
          </Grid>
        )}
      </Grid>
    ),
    header: {
      key: 'confirmDocumentInformation',
      title: 'Подтверждение уполномоченного лица',
    },
    mode: [EntryType.requests, EntryType.registry],
    isRegistred: true,
  },
  {
    dataFormatter: (item) => (
      <Grid container spacing="4">
        {item.testReportInformation?.testReportNumber && (
          <Grid item>
            <p>{`Номер протокола: ${item.testReportInformation?.testReportNumber}`}</p>
          </Grid>
        )}
        {item.testReportInformation?.testReportDate && (
          <Grid item marginLeft={2}>
            <p>{`Дата выдачи: ${item.testReportInformation?.testReportDate}`}</p>
          </Grid>
        )}
        {item.testReportInformation?.testReportFile?.id && (
          <Grid item marginLeft={2}>
            <p>
              <span>Файл: </span>
              <a href={`/files/${item.testReportInformation?.testReportFile?.id}`}>
                <Icon icon="download" />
                <span>{item.testReportInformation?.testReportFile?.name}</span>
              </a>
            </p>
          </Grid>
        )}
        {item.testReportInformation?.testReportOrganization && (
          <Grid item xs={12}>
            <p>{`Организация, выдавшая протокол: ${item.testReportInformation?.testReportOrganization}`}</p>
          </Grid>
        )}
      </Grid>
    ),
    header: {
      key: 'testReportInformation',
      title: 'Протокол испытаний',
    },
    mode: [EntryType.requests, EntryType.registry],
    isRegistred: true,
  },
  {
    dataFormatter: (item) => item.requestNumber,
    header: {
      key: 'requestNumber',
      title: 'Номер заявки',
    },
    mode: [EntryType.requests, EntryType.registry],
    isRegistred: true,
  },
  {
    dataFormatter: (item) => item.requestDate,
    header: {
      key: 'requestDate',
      title: 'Дата регистрации заявки',
    },
    mode: [EntryType.requests, EntryType.registry],
    isRegistred: true,
  },
  {
    dataFormatter: (item) => item.status,
    header: {
      key: 'status',
      title: 'Статус',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const permissionApplicationColumns: InformationTable = [
  {
    dataFormatter: (item) => item.requestRegNumber,
    header: {
      key: 'requestRegNumber',
      title: 'Номер заявления',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.requestRegDate,
    header: {
      key: 'requestRegDate',
      title: 'Дата регистрации заявления',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.requestMethod?.name,
    header: {
      key: 'requestMethod',
      title: 'Способ подачи заявления',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const epguRegistrationBlockColumn: Row & {
  mode: string[];
} = {
  dataFormatter: (item) => getMessageForElkEpgu(item.epguRegistration),
  header: {
    key: 'epguRegistration',
    title: 'Передача в ЕЛК ЕПГУ статуса заявления',
  },
  mode: [EntryType.requests, EntryType.registry],
};

export const permissionTempBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.permissionTempNumber,
    header: {
      key: 'permissionTempNumber',
      title: 'Номер разрешения (временный)',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.permissionTempDate,
    header: {
      key: 'permissionTempDate',
      title: 'Дата выдачи разрешения (временного)',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) => item.permissionTempValidDate,
    header: {
      key: 'permissionTempValidDate',
      title: 'Срок действия',
    },
    mode: [EntryType.requests],
  },
];

export const permissionBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.permissionValidDate,
    header: {
      key: 'permissionValidDate',
      title: 'Срок действия разрешения',
    },
    mode: [EntryType.registry],
  },
  {
    dataFormatter: (item) => item.packSerialNumber,
    header: {
      key: 'packSerialNumber',
      title: 'Разрешение на серию/партию',
    },
    mode: [EntryType.registry],
    isPermitted: true,
  },
];

export const erulBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.permissionNumber,
    header: {
      key: 'permissionNumber',
      title: 'Номер разрешения ЕРУЛ',
    },
    mode: [EntryType.registry, EntryType.requests],
  },
  {
    dataFormatter: (item) => item.permissionDate,
    header: {
      key: 'permissionDate',
      title: 'Дата выдачи разрешения ЕРУЛ',
    },
    mode: [EntryType.registry, EntryType.requests],
  },
];

export const erulWithStatusBlockColumns: InformationTable = [
  {
    dataFormatter: (item) =>
      !!item.permissionNumber &&
      `${item.permissionNumber}${
        !item.erulManualChanged &&
        item.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_SUCCESS
          ? ' (получен автоматически)'
          : ''
      }`,
    header: {
      key: 'permissionNumber',
      title: 'Номер разрешения ЕРУЛ',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      !!item.permissionDate &&
      `${item.permissionDate}${
        !item.erulManualChanged &&
        item.erulRegistration?.status.code === ErulRegistrationCodes.ERUL_SUCCESS
          ? ' (получен автоматически)'
          : ''
      }`,
    header: {
      key: 'permissionDate',
      title: 'Дата выдачи разрешения ЕРУЛ',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
];

export const rejectBlockColumns: InformationTable = [
  {
    dataFormatter: (item) => item.rejectComment,
    header: {
      key: 'rejectComment',
      title: 'Комментарий при отклонении',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
  {
    dataFormatter: (item) =>
      !!item.rejectFile?.id && (
        <a href={`/files/${item.rejectFile.id}`} download>
          <Icon icon="download" />
          <span>{item.rejectFile.name}</span>
        </a>
      ),
    header: {
      key: 'rejectFile',
      title: 'Файл основания отказа',
    },
    mode: [EntryType.requests, EntryType.registry],
  },
];
